import md5 from 'md5'
import { observable, runInAction } from 'mobx'

import { Store } from '../general'
import { StorageKey, StorageStore } from '../storage'

import { Api, ApiError } from '../api'
import { I18nStore } from '../i18n'

import { UserInviteData } from '../data'

import Profile, { Data as ProfileData } from './profile'

interface RootStore {
  api: Api
  i18n: I18nStore
  storage: StorageStore
}

interface Data {
  email?: string
  token?: string
  profile?: Profile
  loading: boolean
}

interface SignInData {
  email: string
  password: string
}

interface SignUpData extends SignInData {
  token: string
  name: string
}

interface UpdatePasswordData extends SignInData {
  token: string
}

interface AuthResponse {
  token: string
}

interface AuthConfig {
  deeplinkUrl?: string
}

class AuthStore extends Store<RootStore> {
  private data: Data
  private config: AuthConfig

  constructor(store: RootStore, config: AuthConfig) {
    super(store)
    this.data = observable<Data>({ loading: true })
    this.config = config
  }

  get token() {
    return this.data.token
  }

  get email() {
    return this.data.email
  }

  get profile() {
    return this.data.profile
  }

  get isSignedIn() {
    return !!this.data.token
  }

  get isLoaded() {
    return !this.data.loading
  }

  async rememberEmail(email: string) {
    const { storage } = this.store
    storage?.get(StorageKey.Email, undefined, async (data?: string) => {
      const emails = JSON.parse(data || '[]')
      if (emails.includes(email)) {
        return
      }
      emails.push(email)
      await storage?.set(StorageKey.Email, JSON.stringify(emails))
    })
  }

  async forgetEmail(email: string) {
    const { storage } = this.store
    storage?.get(StorageKey.Email, undefined, async (data?: string) => {
      const emails = JSON.parse(data || '[]').filter((storedEmail: string) => storedEmail !== email)
      await storage?.set(StorageKey.Email, JSON.stringify(emails))
    })
  }

  async signIn(data: SignInData) {
    const { api } = this.store
    const { token } = await api.post<AuthResponse>('login', { ...data })
    await this.updateAuth(data.email, token)
  }

  async signUp(data: SignUpData) {
    const { api } = this.store
    const { token, ...otherData } = data
    const response = await api.post<AuthResponse>(
      'register',
      {
        ...otherData,
      },
      {
        token,
      },
    )
    await this.updateAuth(data.email, response.token)
  }

  async forgotPassword(email: string) {
    const { api } = this.store
    const { deeplinkUrl } = this.config
    const url = `${deeplinkUrl}/password`
    await api.post<AuthResponse>('forgot_password', { url, email })
  }

  async updatePassword(data: UpdatePasswordData) {
    const { api } = this.store
    const { token, password } = data
    const response = await api.put<AuthResponse>(
      'reset_password',
      { password },
      {
        token,
      },
    )
    await this.updateAuth(data.email, response.token)
  }

  async inviteUser(companyId: number, data: UserInviteData) {
    const { deeplinkUrl } = this.config
    const url = `${deeplinkUrl}/name?next=CompanyHome&companyId=${companyId}`
    const body = {
      ...data,
      url,
    }
    return this.store.api.post<void>(`company/${companyId}/users/invite`, body)
  }

  async reinviteUser(companyId: number, data: UserInviteData) {
    const { deeplinkUrl } = this.config
    const { id } = data
    const url = `${deeplinkUrl}/name?next=CompanyHome&companyId=${companyId}`
    const body = {
      url,
    }
    return this.store.api.post<void>(`company/${companyId}/users/${id}/resend_invite`, body)
  }

  async rememberAuth() {
    if (!this.token || !this.email) throw new Error('Remember auth failed: Not signed in')
    const { storage } = this.store
    await storage?.set(md5(this.email), this.token)
    await storage?.set(StorageKey.Auth, this.email)
  }

  async forgetAuth(email: string) {
    const { storage } = this.store
    await storage?.delete(md5(email))
    await storage?.delete(StorageKey.Auth)
  }

  async signOut() {
    const { api } = this.store
    try {
      await api.post('logout')
    } catch (err) {
      const error = err as Error
      const { message } = error
      console.debug(`Loging failed: ${message}`)
    }
    const { storage } = this.store
    await storage?.delete(md5(`${this.email}`))
    await storage?.delete(StorageKey.Auth)
    runInAction(() => {
      delete this.data.token
      delete this.data.email
      delete this.data.profile
    })
  }

  async init() {
    return new Promise<void>((resolve) => {
      const { storage } = this.store
      storage?.get(StorageKey.Auth, undefined, async (email) => {
        if (email) {
          storage?.get(md5(email), undefined, async (token?: string) => {
            await this.updateAuth(email, token)
            console.debug('AUTH: Logged in as: ', email)
            resolve()
          })
        } else {
          runInAction(() => {
            delete this.data.token
            this.data.loading = false
          })
          resolve()
        }
      })
    })
  }

  async updateAuth(email?: string, token?: string) {
    try {
      const profile = new Profile(
        this.store,
        await this.store.api.get<ProfileData>('profile', token ? { token } : undefined),
      )
      if (profile.language && profile.language !== this.store.i18n.language) {
        this.store.i18n.changeLanguage(profile.language)
      }
      runInAction(() => {
        this.data.token = token
        this.data.email = email
        console.debug('AUTH: Token: ', token)
        this.data.profile = profile
        this.data.loading = false
      })
    } catch (error) {
      if ((error as ApiError).status === 401) await this.signOut()
    }
  }
}

export default AuthStore
